import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent as EventoDeMouse } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@ds/core';
import { ACEITA_POR_GRUPO } from '@/data/brain';
import type { ProjetoDoBrain } from '@/data/brain';
import { s } from './classes';
import { estiloAncorado, estiloDeSubmenu, useFecharPorFora, useRetanguloDaAncora, useSetasNoMenu } from './ui/camadas';

/**
 * O menu "+" do campo de mensagem — porte do `BrainComposerPlusMenu` do Brain
 * original, sem GitHub, Google Drive e conectores (fora do escopo) e sem vídeo e
 * áudio como anexo, que o servidor não lê.
 */

export type FerramentaDoCampo = 'web' | 'documentos' | null;

type Submenu = 'anexar' | 'projeto' | 'aprender';

const TIPOS_DE_ANEXO = [
    { id: 'imagem', rotulo: 'Imagem', icone: 'image', aceita: ACEITA_POR_GRUPO.imagem },
    { id: 'nota', rotulo: 'Nota fiscal ou recibo', icone: 'receipt', aceita: ACEITA_POR_GRUPO.notaFiscal },
    { id: 'documento', rotulo: 'Documento', icone: 'file-text', aceita: ACEITA_POR_GRUPO.documento },
];

export interface MenuDoMaisProps {
    aberto: boolean;
    ancora: HTMLElement | null;
    /** Com conversa aberta o menu abre para cima, acima do campo preso embaixo. */
    acoplado: boolean;
    aoFechar: () => void;
    aoEscolherTipo: (aceita: string) => void;
    aoCapturarTela: () => void;
    ferramenta: FerramentaDoCampo;
    aoEscolherFerramenta: (f: Exclude<FerramentaDoCampo, null>) => void;
    projetos: ProjetoDoBrain[];
    projetoAtivo: string | null;
    aoEscolherProjeto: (id: string | null) => void;
    aoCriarProjeto: (nome: string) => Promise<void>;
    aoAprenderLink: (url: string) => Promise<void>;
}

export function MenuDoMais({
    aberto, ancora, acoplado, aoFechar, aoEscolherTipo, aoCapturarTela, ferramenta, aoEscolherFerramenta,
    projetos, projetoAtivo, aoEscolherProjeto, aoCriarProjeto, aoAprenderLink,
}: MenuDoMaisProps) {
    const [submenu, setSubmenu] = useState<Submenu | null>(null);
    const [ancoraDoSubmenu, setAncoraDoSubmenu] = useState<HTMLElement | null>(null);
    const [url, setUrl] = useState('');
    const [lendo, setLendo] = useState(false);
    const [nomeDoProjeto, setNomeDoProjeto] = useState('');
    const [criando, setCriando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const principal = useRef<HTMLDivElement>(null);
    const lateral = useRef<HTMLDivElement>(null);
    const setas = useSetasNoMenu();

    const rect = useRetanguloDaAncora(ancora, aberto);
    const rectDoSubmenu = useRetanguloDaAncora(ancoraDoSubmenu, Boolean(ancoraDoSubmenu));

    const fecharTudo = () => {
        setSubmenu(null);
        setAncoraDoSubmenu(null);
        setUrl('');
        setNomeDoProjeto('');
        setErro(null);
        aoFechar();
    };

    useEffect(() => {
        if (!aberto) {
            setSubmenu(null);
            setAncoraDoSubmenu(null);
            setUrl('');
            setNomeDoProjeto('');
            setErro(null);
        }
    }, [aberto]);

    useFecharPorFora(
        aberto,
        (alvo) => Boolean(ancora?.contains(alvo) || principal.current?.contains(alvo) || lateral.current?.contains(alvo)),
        fecharTudo,
        ancora,
    );

    /* Foco no primeiro item ao abrir, como um menu de verdade. */
    useLayoutEffect(() => {
        if (!aberto || !rect) return;
        principal.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- só na abertura
    }, [aberto, Boolean(rect)]);

    const abrirSubmenu = (e: EventoDeMouse<HTMLElement>, tipo: Submenu, focar = false) => {
        const el = e.currentTarget;
        setAncoraDoSubmenu(el);
        setSubmenu(tipo);
        setErro(null);
        if (focar) setTimeout(() => lateral.current?.querySelector<HTMLElement>('[role="menuitem"], input')?.focus(), 0);
    };

    const aprender = async () => {
        const limpo = url.trim();
        if (!limpo) {
            setErro('Informe uma URL.');
            return;
        }
        setLendo(true);
        setErro(null);
        try {
            await aoAprenderLink(limpo);
            fecharTudo();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível ler a URL.');
        } finally {
            setLendo(false);
        }
    };

    const criarProjeto = async () => {
        const limpo = nomeDoProjeto.trim();
        if (!limpo) {
            setErro('Informe o nome do projeto.');
            return;
        }
        setCriando(true);
        setErro(null);
        try {
            await aoCriarProjeto(limpo);
            fecharTudo();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Erro ao criar projeto.');
        } finally {
            setCriando(false);
        }
    };

    if (!aberto) return null;

    const larguraDoMenu = 224;
    const estiloPrincipal: CSSProperties = (() => {
        if (!rect) return { visibility: 'hidden' };
        const folga = 6;
        const borda = 12;
        let left = rect.left;
        if (left + larguraDoMenu > window.innerWidth - borda) left = window.innerWidth - larguraDoMenu - borda;
        left = Math.max(borda, left);
        if (acoplado) {
            return {
                ...estiloAncorado(rect, { minWidth: larguraDoMenu, placement: 'above', offset: folga }),
                left, width: larguraDoMenu, maxHeight: Math.min(340, Math.max(120, rect.top - borda - folga)), overflowY: 'auto',
            };
        }
        const top = rect.bottom + folga;
        return {
            ...estiloAncorado(rect, { minWidth: larguraDoMenu, placement: 'below', offset: folga }),
            left, width: larguraDoMenu, maxHeight: Math.min(340, Math.max(100, window.innerHeight - top - borda)),
        };
    })();

    const linha = { padding: '8px 12px', fontSize: 12.5 } satisfies CSSProperties;

    const conteudoDoSubmenu = () => {
        if (submenu === 'anexar') {
            return TIPOS_DE_ANEXO.map((t) => (
                <button key={t.id} type="button" role="menuitem" className={s.menuRow} style={linha} onClick={() => { aoEscolherTipo(t.aceita); fecharTudo(); }}>
                    <span className={s.menuRowIcon}><Icon name={t.icone} size={15} /></span>
                    {t.rotulo}
                </button>
            ));
        }
        if (submenu === 'projeto') {
            return (
                <>
                    <button type="button" role="menuitem" className={s.menuRow} style={linha} onClick={() => { aoEscolherProjeto(null); fecharTudo(); }}>
                        <span className={s.menuRowIcon}><Icon name="messages-square" size={14} /></span>
                        <span style={{ flex: 1, minWidth: 0 }}>Sem projeto</span>
                        {projetoAtivo === null && <span className={s.menuRowRight}><Icon name="check" size={13} /></span>}
                    </button>
                    {projetos.map((p) => (
                        <button key={p.id} type="button" role="menuitem" className={s.menuRow} style={linha} onClick={() => { aoEscolherProjeto(p.id); fecharTudo(); }}>
                            <span className={s.menuRowIcon} style={{ width: 8, height: 8, borderRadius: '50%', background: '#78716c' }} />
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nome}</span>
                            {p.id === projetoAtivo && <span className={s.menuRowRight}><Icon name="check" size={13} /></span>}
                        </button>
                    ))}
                    <div className={s.menuDivider} />
                    <div className="brain-menu__inline-form">
                        <input
                            type="text" className="brain-menu__inline-input" placeholder="Novo projeto…" aria-label="Nome do novo projeto" maxLength={80}
                            value={nomeDoProjeto} onChange={(e) => setNomeDoProjeto(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') void criarProjeto(); }}
                        />
                        <button type="button" className="brain-menu__inline-btn" onClick={() => void criarProjeto()} disabled={criando} aria-label="Criar projeto">
                            <Icon name={criando ? 'loader-circle' : 'plus'} size={14} />
                        </button>
                    </div>
                </>
            );
        }
        if (submenu === 'aprender') {
            return (
                <div className="brain-menu__learn">
                    <p className="brain-menu__learn-hint">Cole uma URL para a VecchIA ler e usar a página como contexto desta mensagem.</p>
                    <input
                        type="url" className="brain-menu__inline-input brain-menu__inline-input--full" placeholder="https://exemplo.com/artigo" aria-label="Endereço da página"
                        value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void aprender(); }}
                    />
                    <button type="button" className="brain-menu__learn-btn" onClick={() => void aprender()} disabled={lendo || !url.trim()}>
                        <Icon name={lendo ? 'loader-circle' : 'graduation-cap'} size={14} />
                        Aprender desta URL
                    </button>
                </div>
            );
        }
        return null;
    };

    const temSubmenu = (tipo: Submenu) => ({
        'aria-haspopup': 'menu' as const,
        'aria-expanded': submenu === tipo,
        onMouseEnter: (e: EventoDeMouse<HTMLElement>) => abrirSubmenu(e, tipo),
        onClick: (e: EventoDeMouse<HTMLElement>) => abrirSubmenu(e, tipo, true),
    });

    return createPortal(
        <>
            {rect && (
                // eslint-disable-next-line jsx-a11y/interactive-supports-focus -- o foco vai para os itens, não para o contêiner
                <div ref={principal} role="menu" aria-label="Adicionar ao campo de mensagem" className={`brain-menu__panel brain-menu__panel--composer ${s.menuPanel}`} style={estiloPrincipal} onKeyDown={setas}>
                    <button type="button" role="menuitem" className={s.menuRow} {...temSubmenu('anexar')}>
                        <span className={s.menuRowIcon}><Icon name="paperclip" size={16} /></span>
                        Adicionar arquivos ou fotos
                        <span className={s.menuRowRight}><Icon name="chevron-right" size={14} /></span>
                    </button>
                    <button type="button" role="menuitem" className={s.menuRow} onClick={() => { fecharTudo(); aoCapturarTela(); }}>
                        <span className={s.menuRowIcon}><Icon name="camera" size={16} /></span>
                        Fazer uma Captura de Tela
                    </button>
                    <button type="button" role="menuitem" className={s.menuRow} {...temSubmenu('projeto')}>
                        <span className={s.menuRowIcon}><Icon name="folder-kanban" size={16} /></span>
                        Adicionar ao Projeto
                        <span className={s.menuRowRight}><Icon name="chevron-right" size={14} /></span>
                    </button>
                    <div className={s.menuDivider} />
                    <button type="button" role="menuitem" className={s.menuRow} {...temSubmenu('aprender')}>
                        <span className={s.menuRowIcon}><Icon name="graduation-cap" size={16} /></span>
                        Aprender
                        <span className={s.menuRowRight}><Icon name="chevron-right" size={14} /></span>
                    </button>
                    <div className={s.menuDivider} />
                    <button type="button" role="menuitem" className={s.menuRow} onClick={() => { aoEscolherFerramenta('web'); fecharTudo(); }}>
                        <span className={s.menuRowIcon}><Icon name="globe" size={16} /></span>
                        Buscar na web
                        {ferramenta === 'web' && <span className={s.menuRowRight}><Icon name="check" size={14} /></span>}
                    </button>
                    <button type="button" role="menuitem" className={s.menuRow} onClick={() => { aoEscolherFerramenta('documentos'); fecharTudo(); }}>
                        <span className={s.menuRowIcon}><Icon name="book-open" size={16} /></span>
                        Buscar em documentos
                        {ferramenta === 'documentos' && <span className={s.menuRowRight}><Icon name="check" size={14} /></span>}
                    </button>
                </div>
            )}
            {submenu && rectDoSubmenu && (
                // eslint-disable-next-line jsx-a11y/interactive-supports-focus -- o foco vai para os itens, não para o contêiner
                <div
                    ref={lateral} role="menu" aria-label={submenu === 'anexar' ? 'Tipos de arquivo' : submenu === 'projeto' ? 'Projetos' : 'Aprender de uma URL'}
                    className={`brain-menu__panel brain-menu__panel--sub ${s.menuPanel}`} onKeyDown={setas}
                    style={estiloDeSubmenu(rectDoSubmenu, { minWidth: submenu === 'aprender' ? 280 : 220, maxHeight: submenu === 'aprender' ? 240 : 360 })}
                >
                    <div className="brain-menu__sub-scroll">
                        {conteudoDoSubmenu()}
                        {erro && <p role="alert" style={{ margin: '6px 12px 8px', fontSize: 11.5, color: '#dc2626' }}>{erro}</p>}
                    </div>
                </div>
            )}
        </>,
        document.body,
    );
}
